import { BRAND_CONFIG } from '@@brand'
import classNames from 'classnames'
import { ReactSVG } from 'react-svg'

import { type LoyaltyStatusLevelInfo, useFetchLoyaltyLevelInfo } from '@entities/Loyalty'

import { formatPrice } from '@shared/uttils'

import styles from './styles.module.scss'

/**
 * Форматирует дату в формате "д месяц"
 */
function formatDayMonth (date: Date): string {
  const months = [
    'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
    'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
  ]
  return `${date.getDate()} ${months[date.getMonth()]}`
}

export function ModalLoyaltyLevels () {
  const { loyaltyStatusInfo } = useFetchLoyaltyLevelInfo()

  if (!loyaltyStatusInfo?.currentLevel || !loyaltyStatusInfo.levels?.length) {
    return null
  }

  const { levels, currentLevel, nextLevel, currentValue } = loyaltyStatusInfo

  // Находим индекс текущего уровня
  const currentLevelIndex = levels.findIndex(
    (level) => level.threshold === currentLevel.threshold
  )

  // Рассчитываем прогресс линии для каждого уровня
  const getLineProgress = (index: number, level: LoyaltyStatusLevelInfo) => {
    if (index < currentLevelIndex) return 1 // Полностью залита
    if (index > currentLevelIndex) return 0 // Не залита
    
    // Текущий уровень - рассчитываем прогресс к следующему
    const nextLevelData = levels[index + 1]
    if (!nextLevelData) return 0
    
    const currentThreshold = level.threshold
    const nextThreshold = nextLevelData.threshold
    const range = nextThreshold - currentThreshold
    
    if (range <= 0) return 0
    
    const progress = (currentValue - currentThreshold) / range
    return Math.max(0, Math.min(1, progress))
  }

  return (
    <div className={styles.levelsModal}>
      {/* Заголовок с текущим кешбэком */}
      <div className={styles.levelsModal__header}>
        <span className={styles.levelsModal__cashbackValue}>
          {currentLevel.preference}{currentLevel.preferencePostfix}
        </span>
        <span className={styles.levelsModal__cashbackLabel}>
          Начислим бонусами за любые покупки
        </span>
      </div>

      {/* Описание текущего уровня */}
      <div className={styles.levelsModal__currentDescription}>
        {currentLevel.description.split('•').filter(Boolean).map((line, index) => (
          <p key={index}>• {line.trim()}</p>
        ))}
      </div>

      {/* Список всех уровней */}
      <div className={styles.levelsModal__levels}>
        {levels.map((level, index) => {
          const isFinished = index <= currentLevelIndex
          const isCurrent = index === currentLevelIndex
          const isLast = index === levels.length - 1
          const lineProgress = getLineProgress(index, level)

          return (
            <div 
              key={index} 
              className={classNames(styles.levelsModal__level, {
                [styles['levelsModal__level--current']]: isCurrent,
                [styles['levelsModal__level--finished']]: isFinished && !isCurrent,
                [styles['levelsModal__level--future']]: !isFinished,
              })}
            >
              {/* Точка и линия */}
              <div className={styles.levelsModal__levelLine}>
                <div 
                  className={classNames(styles.levelsModal__dot, {
                    [styles['levelsModal__dot--current']]: isCurrent,
                    [styles['levelsModal__dot--finished']]: isFinished,
                  })}
                >
                  { isFinished && (
                    <ReactSVG src='/icons/check.svg' />
                  )}
                  
                </div>
                {!isLast && (
                  <div className={styles.levelsModal__line}>
                    <div 
                      className={styles.levelsModal__lineFill}
                      style={{ height: `${lineProgress * 100}%` }}
                    />
                  </div>
                )}
              </div>

              {/* Информация об уровне */}
              <div className={styles.levelsModal__levelContent}>
                <span className={styles.levelsModal__levelName}>{level.name}</span>
                
                {/* Прогресс до следующего уровня (только для текущего) */}
                {isCurrent && nextLevel?.needToNextLevel ? (
                  <div className={styles.levelsModal__progressBlock}>
                    <span className={styles.levelsModal__progressValue}>
                      {formatPrice(nextLevel.needToNextLevel, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                        style: 'decimal',
                      })} {BRAND_CONFIG.currency.symbol}
                    </span>
                    <span className={styles.levelsModal__progressText}>
                      Осталось до начисления {nextLevel.preference}{nextLevel.preferencePostfix} с {formatDayMonth(new Date(nextLevel.nextCalcDate))}
                    </span>
                    <div className={styles.levelsModal__progressBar}>
                      <div 
                        className={styles.levelsModal__progressFill}
                        style={{ 
                          width: `${Math.max((currentValue / (currentValue + nextLevel.needToNextLevel)) * 100, 2)}%` 
                        }}
                      />
                    </div>
                  </div>
                ) : null}

                {/* Описание уровня */}
                <div className={styles.levelsModal__levelDescription}>
                  {level.description.split('•').filter(Boolean).map((line, idx) => (
                    <span key={idx}>• {line.trim()}</span>
                  ))}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
